/**
 * Support Tickets hooks using React Relay
 *
 * These hooks provide access to support ticket data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useQueryLoader,
  usePreloadedQuery,
  PreloadedQuery,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useSupportTicketsRelayQuery as UseSupportTicketsRelayQuery,
  useSupportTicketsRelayQuery$variables,
  SupportTicketFilterInput,
  SupportTicketSortInput,
} from './__generated__/useSupportTicketsRelayQuery.graphql';
import type { useSupportTicketsRelayDirectQuery as UseSupportTicketsRelayDirectQuery } from './__generated__/useSupportTicketsRelayDirectQuery.graphql';
import type { useSupportTicketsRelayPaginationFragment$key } from './__generated__/useSupportTicketsRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Pagination fragment for support tickets list
 */
const SupportTicketsListPaginationFragment = graphql`
  fragment useSupportTicketsRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useSupportTicketsRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "SupportTicketFilterInput" }
    sort: { type: "[SupportTicketSortInput!]" }
    filterTags: { type: "[String!]" }
  ) {
    listSupportTickets(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      filterTags: $filterTags
    )
      @connection(
        key: "useSupportTicketsRelayPaginationFragment_listSupportTickets"
        filters: ["filter", "sort", "filterTags"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          state
          content
          created
          closedDate
          owner
          tags
          user {
            id
            phone
            fullName
            firstName
            lastName
          }
          hardware {
            id
            oldId
            number
          }
          rental {
            id
            oldId
          }
          system {
            id
            oldId
            name
          }
          organizationId
          systemId
          systemOldId
        }
      }
    }
  }
`;

/**
 * Query for paginated support tickets list (uses fragment for Suspense hooks)
 */
const SupportTicketsListQuery = graphql`
  query useSupportTicketsRelayQuery(
    $first: Int
    $after: String
    $filter: SupportTicketFilterInput
    $sort: [SupportTicketSortInput!]
    $filterTags: [String!]
  ) {
    ...useSupportTicketsRelayPaginationFragment
      @arguments(
        first: $first
        after: $after
        filter: $filter
        sort: $sort
        filterTags: $filterTags
      )
  }
`;

/**
 * Direct query for support tickets list (for non-Suspense fetchQuery usage)
 * This query doesn't use fragments so fetchQuery returns the actual data
 */
const SupportTicketsListDirectQuery = graphql`
  query useSupportTicketsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: SupportTicketFilterInput
    $sort: [SupportTicketSortInput!]
    $filterTags: [String!]
  ) {
    listSupportTickets(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      filterTags: $filterTags
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          state
          content
          created
          closedDate
          owner
          tags
          user {
            id
            phone
            fullName
            firstName
            lastName
          }
          hardware {
            id
            oldId
            number
          }
          rental {
            id
            oldId
          }
          system {
            id
            oldId
            name
          }
          organizationId
          systemId
          systemOldId
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { SupportTicketFilterInput, SupportTicketSortInput };

/**
 * User type from GraphQL support ticket
 */
export interface SupportTicketUserGraphQL {
  id: string;
  phone: string;
  fullName?: string | null;
  firstName: string;
  lastName: string;
}

/**
 * Hardware type from GraphQL support ticket
 */
export interface SupportTicketHardwareGraphQL {
  id: string;
  oldId?: string | null;
  number?: number | null;
}

/**
 * Rental type from GraphQL support ticket
 */
export interface SupportTicketRentalGraphQL {
  id: string;
  oldId?: string | null;
}

/**
 * System type from GraphQL support ticket
 */
export interface SupportTicketSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

/**
 * Support Ticket type from GraphQL - matches the fragment fields
 */
export interface SupportTicketGraphQL {
  id: string;
  oldId?: string | null;
  state?: number | null;
  content?: string | null;
  created?: string | null;
  closedDate?: string | null;
  owner?: string | null;
  tags?: readonly string[] | null;
  user?: SupportTicketUserGraphQL | null;
  hardware?: SupportTicketHardwareGraphQL | null;
  rental?: SupportTicketRentalGraphQL | null;
  system?: SupportTicketSystemGraphQL | null;
  organizationId?: string | null;
  systemId?: string | null;
  systemOldId?: string | null;
}

/**
 * Simplified filter options for common use cases
 */
export interface SupportTicketQueryFilter {
  systemId?: string;
  systemIds?: string[]; // Filter by multiple systems (mapped to systemId `in`)
  systemOldId?: string;
  organizationId?: string;
  state?: number; // 0=open, 1=closed
  tags?: string[]; // Tag names filter (separate query argument)
  pattern?: string; // Search content
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching support tickets list using Relay
 *
 * This hook uses Relay's useLazyLoadQuery which suspends while loading.
 * Wrap with Suspense boundary.
 *
 * @example
 * function SupportTicketList() {
 *   const { data, loadNext, hasNext, isLoadingNext } = useSupportTicketsRelay({
 *     systemId: 'uuid',
 *   });
 *
 *   return (
 *     <>
 *       {data.map(ticket => <div key={ticket.id}>{ticket.content}</div>)}
 *       {hasNext && <button onClick={() => loadNext(100)}>Load More</button>}
 *     </>
 *   );
 * }
 *
 * // With Suspense
 * <Suspense fallback={<Loading />}>
 *   <SupportTicketList />
 * </Suspense>
 */
export function useSupportTicketsRelay(
  filter?: SupportTicketQueryFilter,
  options?: {
    limit?: number;
    sort?: SupportTicketSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED', order: 'DESC' }];

  const builtFilter = buildRelayFilter(filter);
  const variables: useSupportTicketsRelayQuery$variables = {
    first: limit,
    sort,
    // Only include filter if it's defined (not null/undefined)
    ...(builtFilter ? { filter: builtFilter } : {}),
    ...(filter?.tags && filter.tags.length > 0 ? { filterTags: filter.tags } : {}),
  };

  // Initial query - suspends while loading
  const queryData = useLazyLoadQuery<UseSupportTicketsRelayQuery>(
    SupportTicketsListQuery,
    variables
  );

  // Pagination fragment for load more
  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseSupportTicketsRelayQuery,
    useSupportTicketsRelayPaginationFragment$key
  >(SupportTicketsListPaginationFragment, queryData);

  // Extract nodes from edges
  const nodes =
    data.listSupportTickets?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as SupportTicketGraphQL[],
    count: data.listSupportTickets?.count,
    pageInfo: data.listSupportTickets?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

// ============================================================================
// Non-Suspense Hooks (with loading state)
// ============================================================================

/**
 * Hook that returns a query loader for support tickets list
 *
 * This provides explicit loading state without requiring Suspense.
 * Call `loadQuery` to start fetching, then pass `queryRef` to a child component.
 */
export function useSupportTicketsRelayLoader(initialFilter?: SupportTicketQueryFilter) {
  const initialBuiltFilter = buildRelayFilter(initialFilter);
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<UseSupportTicketsRelayQuery>(
    SupportTicketsListQuery,
    initialFilter
      ? {
          first: 100,
          sort: [{ field: 'CREATED', order: 'DESC' }],
          ...(initialBuiltFilter ? { filter: initialBuiltFilter } : {}),
          ...(initialFilter?.tags && initialFilter.tags.length > 0
            ? { filterTags: initialFilter.tags }
            : {}),
        }
      : undefined
  );

  const load = (
    filter?: SupportTicketQueryFilter,
    options?: { limit?: number; sort?: SupportTicketSortInput[] }
  ) => {
    const limit = options?.limit ?? 100;
    const sort = options?.sort ?? [{ field: 'CREATED', order: 'DESC' }];
    const builtFilter = buildRelayFilter(filter);

    loadQuery({
      first: limit,
      sort,
      ...(builtFilter ? { filter: builtFilter } : {}),
      ...(filter?.tags && filter.tags.length > 0 ? { filterTags: filter.tags } : {}),
    });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded support tickets query
 *
 * Use with useSupportTicketsRelayLoader. Still suspends, but the data
 * is already loading so suspension is minimal.
 */
export function useSupportTicketsRelayPreloaded(
  queryRef: PreloadedQuery<UseSupportTicketsRelayQuery>
) {
  const queryData = usePreloadedQuery<UseSupportTicketsRelayQuery>(
    SupportTicketsListQuery,
    queryRef
  );

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseSupportTicketsRelayQuery,
    useSupportTicketsRelayPaginationFragment$key
  >(SupportTicketsListPaginationFragment, queryData);

  const nodes =
    data.listSupportTickets?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as SupportTicketGraphQL[],
    count: data.listSupportTickets?.count,
    pageInfo: data.listSupportTickets?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for support tickets data
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch, isFetched, count, hasMore } = useSupportTicketsRelayQuery({
 *   systemId: 'uuid',
 *   state: 0, // open tickets
 * });
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.map(t => t.content)}</div>;
 */
export function useSupportTicketsRelayQuery(
  filter?: SupportTicketQueryFilter,
  options?: {
    limit?: number;
    sort?: SupportTicketSortInput[];
    enabled?: boolean;
    after?: string | null;
  }
) {
  const [data, setData] = useState<SupportTicketGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;
  // Convert null to undefined to avoid sending null to GraphQL
  const after = options?.after || undefined;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const builtFilter = buildRelayFilter(filter);
        // Build variables object, explicitly excluding null/undefined values
        // to prevent GraphQL from receiving null parameters
        const variables: Record<string, unknown> = {
          first: limit,
          sort,
        };
        if (after) {
          variables.after = after;
        }
        if (builtFilter) {
          variables.filter = builtFilter;
        }
        // filterTags is a separate argument
        if (filter?.tags && filter.tags.length > 0) {
          variables.filterTags = filter.tags;
        }

        const result = await fetchQuery<UseSupportTicketsRelayDirectQuery>(
          relayEnvironment,
          SupportTicketsListDirectQuery,
          variables as useSupportTicketsRelayQuery$variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listSupportTickets?.edges?.map((edge) => edge?.node).filter(Boolean) ??
            [];
          setData(nodes as SupportTicketGraphQL[]);
          setCount(result.listSupportTickets?.count ?? undefined);
          setHasMore(result.listSupportTickets?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listSupportTickets?.pageInfo?.endCursor ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch support tickets')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, after, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds a Relay/GraphQL SupportTicketFilterInput from the simplified filter interface
 * Note: tags filtering is handled as a separate query argument (filterTags)
 */
function buildRelayFilter(
  filter?: SupportTicketQueryFilter
): SupportTicketFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: SupportTicketFilterInput = {};

  if (filter.systemIds && filter.systemIds.length > 0) {
    relayFilter.systemId = { in: filter.systemIds };
  } else if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.state !== undefined) {
    relayFilter.state = { eq: filter.state };
  }
  // Pattern search - search by content
  if (filter.pattern) {
    const pattern = `%${filter.pattern}%`;
    relayFilter.content = { ilike: pattern };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

export { buildRelayFilter };
